import {
  createReference,
  formatCoding,
  formatQuantity,
  getReferenceString,
  MedplumClient,
} from "@medplum/core";
import {
  Coding,
  Observation,
  ObservationComponent,
  Patient,
  Practitioner,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  Reference,
} from "@medplum/fhirtypes";

import { formatISO } from "date-fns";
import { mapValues, pick } from "lodash";

import { Systems } from "@/fhir/enums/systems";

export interface QuestionnaireResponseItemAnswerCoding extends QuestionnaireResponseItemAnswer {
  valueCoding: Coding; // make required, as it's not in QuestionnaireResponseItemAnswer
}

interface QuestionnaireResponseItemCoding<FieldNameType extends string = string>
  extends QuestionnaireResponseItem {
  linkId: `${FieldNameType}-coding`;
  answer: QuestionnaireResponseItemAnswerCoding[];
}

interface QuestionnaireResponseItemValue<FieldNameType extends string = string>
  extends QuestionnaireResponseItem {
  linkId: `${FieldNameType}-value`;
  answer: QuestionnaireResponseItemAnswer[];
}

interface QuestionnaireResponseItemText<FieldNameType extends string = string>
  extends QuestionnaireResponseItem {
  linkId: `${FieldNameType}-text`;
  answer: QuestionnaireResponseItemAnswer[];
}

export interface QuestionnaireResponseItemGroup<FieldNameType extends string = string>
  extends QuestionnaireResponseItem {
  linkId: `${FieldNameType}-group`;
  item: Array<
    | QuestionnaireResponseItemCoding<FieldNameType>
    | QuestionnaireResponseItemValue<FieldNameType>
    | QuestionnaireResponseItemText<FieldNameType>
    | QuestionnaireResponseItemGroup<FieldNameType>
  >;
}

// Note: to be used for Observation.component,
// NOT Observation.hasMember, as the latter can be any-level deep.
export function qResponseGroup({
  parentResponseItem,
  components = [],
}: {
  parentResponseItem: QuestionnaireResponseItemGroup;
  components?: QuestionnaireResponseItemGroup[];
}): QuestionnaireResponseItemGroup {
  return {
    ...parentResponseItem,
    item: [...parentResponseItem.item, ...components],
  };
}

export function qResponseQuantity<FieldNameType extends string>({
  fieldName,
  system = Systems.LOINC,
  code,
  display,
  value,
  valueUnitSystem = Systems.UOM,
  valueUnit,
  valueCode,
}: {
  fieldName: FieldNameType;
  system?: string;
  code: string;
  display: string;
  value: number;
  valueUnitSystem?: string;
  valueUnit: string;
  valueCode: string;
}): QuestionnaireResponseItemGroup<FieldNameType> {
  return {
    linkId: `${fieldName}-group`,
    item: [
      {
        linkId: `${fieldName}-coding`,
        answer: [
          {
            valueCoding: {
              system,
              code,
              display,
            },
          },
        ],
      },
      {
        linkId: `${fieldName}-value`,
        answer: [
          {
            valueQuantity: {
              value,
              system: valueUnitSystem,
              unit: valueUnit,
              code: valueCode,
            },
          },
        ],
      },
    ],
  };
}

export function qResponseString<FieldNameType extends string>({
  fieldName,
  system = Systems.LOINC,
  code,
  display,
  value,
}: {
  fieldName: FieldNameType;
  system?: string;
  code: string;
  display: string;
  value: string;
}): QuestionnaireResponseItemGroup<FieldNameType> {
  return {
    linkId: `${fieldName}-group`,
    item: [
      {
        linkId: `${fieldName}-coding`,
        answer: [
          {
            valueCoding: {
              system,
              code,
              display,
            },
          },
        ],
      },
      {
        linkId: `${fieldName}-value`,
        answer: [
          {
            valueString: value,
          },
        ],
      },
    ],
  };
}

export function qResponseInteger<FieldNameType extends string>({
  fieldName,
  system = Systems.LOINC,
  code,
  display,
  value,
}: {
  fieldName: FieldNameType;
  system?: string;
  code: string;
  display: string;
  value: number;
}): QuestionnaireResponseItemGroup<FieldNameType> {
  return {
    linkId: `${fieldName}-group`,
    item: [
      {
        linkId: `${fieldName}-coding`,
        answer: [
          {
            valueCoding: {
              system,
              code,
              display,
            },
          },
        ],
      },
      {
        linkId: `${fieldName}-value`,
        answer: [
          {
            valueInteger: value,
          },
        ],
      },
    ],
  };
}

export function qResponseCoding<FieldNameType extends string>({
  fieldName,
  system = Systems.LOINC,
  code,
  display,
  value,
  text,
}: {
  fieldName: FieldNameType;
  system?: string;
  code: string;
  display: string;
  value?: Coding;
  text?: string;
}): QuestionnaireResponseItemGroup<FieldNameType> {
  return {
    linkId: `${fieldName}-group`,
    item: [
      {
        linkId: `${fieldName}-coding`,
        answer: [
          {
            valueCoding: {
              system,
              code,
              display,
            },
          },
        ],
      },
      {
        linkId: `${fieldName}-value`,
        answer: [
          {
            valueCoding: value,
          },
        ],
      },
      {
        linkId: `${fieldName}-text`,
        answer: [{ valueString: text }],
      },
    ],
  };
}

export function qResponseDate<FieldNameType extends string>({
  fieldName,
  system = Systems.LOINC,
  code,
  display,
  value,
}: {
  fieldName: FieldNameType;
  system?: string;
  code: string;
  display: string;
  value: string;
}): QuestionnaireResponseItemGroup<FieldNameType> {
  return {
    linkId: `${fieldName}-group`,
    item: [
      {
        linkId: `${fieldName}-coding`,
        answer: [
          {
            valueCoding: {
              system,
              code,
              display,
            },
          },
        ],
      },
      {
        linkId: `${fieldName}-value`,
        answer: [
          {
            valueDate: value,
          },
        ],
      },
    ],
  };
}

export type ResponseFnMappingType<T> = Record<
  string,
  (
    form: T,
    fieldName: keyof ResponseFnMappingType<T>,
  ) => QuestionnaireResponseItemGroup<keyof ResponseFnMappingType<T>> | undefined | null
>;

export function toQuestionnaireResponse<T>(
  form: T,
  responseFnMapping: ResponseFnMappingType<T>,
): QuestionnaireResponse {
  return {
    resourceType: "QuestionnaireResponse",
    status: "in-progress",
    item: Object.keys(responseFnMapping)
      .map((fieldName) => {
        const response = responseFnMapping[fieldName](form, fieldName);
        if (response === null || response === undefined) {
          return null;
        }
        return response;
      })
      .filter((item) => item !== null) as QuestionnaireResponseItem[],
  };
}

export type QuestionnaireSystemType = `http://quiltedhealth.com/forms/${string}`;

export interface ObservationInternalCodingType<FieldName extends string = string> {
  system: Systems.QH;
  code: `forms/${string}/${FieldName}`;
}

export function generateObservationInternalCoding<FieldName extends string>(
  questionnaire: QuestionnaireSystemType,
  fieldName: FieldName,
): ObservationInternalCodingType<FieldName> {
  // Convert "http://quiltedhealth.com/forms/prenatal-flowsheet" to "prenatal-flowsheet":
  // TODO: alternatively, store that as coding in top group.
  const formId = questionnaire.replace(/^http:\/\/quiltedhealth\.com\/forms\//, "");

  return {
    system: Systems.QH,
    code: `forms/${formId}/${fieldName}`,
  };
}

export function getObservationInternalCoding(
  observation: Observation,
): ObservationInternalCodingType | undefined {
  return observation.code.coding?.find(
    (coding): coding is ObservationInternalCodingType =>
      coding.system === Systems.QH && Boolean(coding.code?.startsWith("forms/")),
  );
}

export function belongsToForm(observation: Observation, formId: string): boolean {
  return Boolean(getObservationInternalCoding(observation)?.code.startsWith(`forms/${formId}/`));
}

export function isParentObservation(observation: Observation): boolean {
  const qhCoding = getObservationInternalCoding(observation);
  return Boolean(qhCoding?.code?.endsWith("/panel"));
}

function parseObservationValue(value: QuestionnaireResponseItemAnswer | undefined) {
  if (value?.valueCoding) {
    // This transformation is required as the 'valueCodeableConcept' relies on
    // 'valueCoding' present in the 'QuestionnaireResponseItemAnswer'
    return { valueCodeableConcept: { coding: [value.valueCoding] } };
  }
  return pick(value, [
    "valueBoolean",
    "valueDecimal",
    "valueInteger",
    "valueDate",
    "valueDateTime",
    "valueTime",
    "valueString",
    "valueUri",
    "valueAttachment",
    "valueQuantity",
    "valueReference",
    "valueCodeableConcept",
  ]);
}

type PartialObservationWithStatus = Partial<Observation> & Pick<Observation, "status">;

export function toObservations({
  response,
  observation,
  exclude,
}: {
  response: QuestionnaireResponse;
  observation: PartialObservationWithStatus;
  exclude?: string[];
}) {
  const effectiveDateTime = new Date();
  const observations: Observation[] = [];

  if (!response.item) return observations;

  if (!response.questionnaire) {
    throw new Error("questionnaire field not found in response");
  }

  if (!response.questionnaire.startsWith("http://quiltedhealth.com/forms/")) {
    throw new Error(`questionnaire field has unexpected value: ${response.questionnaire}`);
  }

  for (const item of response.item) {
    if (!item.linkId?.endsWith("-group")) continue;
    const fieldName = item.linkId.replace(/-group$/, "");
    if (exclude && exclude.includes(fieldName)) continue;

    // If the item is a group, assume it has a coding and a value:
    const codedItem = item as QuestionnaireResponseItemGroup;
    const valueCoding = codedItem.item.find((d): d is QuestionnaireResponseItemCoding =>
      d.linkId.endsWith("-coding"),
    )!.answer[0].valueCoding!;
    const value = codedItem.item.find((d): d is QuestionnaireResponseItemValue =>
      d.linkId.endsWith("-value"),
    )?.answer?.[0];
    const components = codedItem.item.filter((d): d is QuestionnaireResponseItemGroup =>
      d.linkId.endsWith("-group"),
    );
    const observationComponent: ObservationComponent[] = components.map((subItem) => {
      const subFieldName = subItem.linkId.replace(/-group$/, "");
      const componentCoding = subItem.item.find((d): d is QuestionnaireResponseItemCoding =>
        d.linkId.endsWith("-coding"),
      )!.answer[0].valueCoding;
      const componentValue = subItem.item.find((d): d is QuestionnaireResponseItemValue =>
        d.linkId.endsWith("-value"),
      )!.answer[0];

      const qhSystemCoding = generateObservationInternalCoding(
        response.questionnaire as QuestionnaireSystemType,
        subFieldName,
      );
      return {
        code: {
          coding: [componentCoding, qhSystemCoding],
          text: componentCoding.display,
        },
        ...parseObservationValue(componentValue),
      };
    });

    // qhSystemCoding is used for upserts:
    const qhSystemCoding = generateObservationInternalCoding(
      response.questionnaire as QuestionnaireSystemType,
      fieldName,
    );
    const fullObservation: Observation = {
      resourceType: "Observation",
      encounter: response.encounter,
      subject: response.subject as Reference<Patient>,
      performer: [response.author as Reference<Practitioner>],
      effectiveDateTime: formatISO(effectiveDateTime),
      code: {
        coding: [valueCoding, qhSystemCoding],
        text: valueCoding.display,
      },
      derivedFrom: [createReference(response)],
      ...parseObservationValue(value),
      ...observation,
      ...(observationComponent ? { component: observationComponent } : {}),
    };
    observations.push(fullObservation);
  }
  return observations;
}

export async function upsertObservations({
  medplum,
  response,
  observation,
  exclude,
}: {
  medplum: MedplumClient;
  response: QuestionnaireResponse;
  observation: PartialObservationWithStatus;
  exclude?: string[];
}) {
  // Step 1: Delete any observations that are not in the response, except the parent observation.
  // Warning: this doesn't handle partial nested observations deletion, which it's not necessary now.
  const existingObservations = await medplum.search("Observation", {
    "derived-from": getReferenceString(response),
  });
  if (existingObservations.entry) {
    await Promise.all(
      existingObservations.entry.map(async (entry) => {
        const obs = entry.resource as Observation;
        if (isParentObservation(obs)) return; // ignore parent observation

        const qhCoding = getObservationInternalCoding(obs);
        const foundInResponse = response.item?.find((item) => {
          if (!item.linkId?.endsWith("-group")) return false;
          const fieldName = item.linkId.replace(/-group$/, "");
          return Boolean(qhCoding?.code?.endsWith(`/${fieldName}`));
        });
        if (!foundInResponse) {
          return medplum.deleteResource("Observation", obs.id!);
        }
      }),
    );
  }

  // Step 2: Give id to the observations with matching code.
  // This is necessary because the `upsertResource` function
  // does not provide id for the resource if it's not modified.
  let observations = toObservations({
    response,
    observation,
    exclude,
  });
  if (existingObservations.entry) {
    observations.forEach((obs) => {
      const qhCoding = getObservationInternalCoding(obs);
      existingObservations.entry?.forEach((entry) => {
        const existingObs = entry.resource as Observation;
        const existingQhCoding = getObservationInternalCoding(existingObs);
        if (qhCoding && existingQhCoding && existingQhCoding.code === qhCoding.code) {
          obs.id = existingObs.id;
        }
      });
    });
  }

  // Step 3: Create the individual observations.
  observations = await Promise.all(
    observations.map(async (obs) => {
      const qhCoding = getObservationInternalCoding(obs) as Coding;
      return medplum.upsertResource(obs, {
        "derived-from": getReferenceString(response),
        code: `${qhCoding.system!}|${qhCoding.code!}`,
      });
    }),
  );

  // Return upserted observations:
  return { observations };
}

/**
 * Formats a QuestionnaireResponseItemAnswer value as a string.
 * Based on Medplum's formatObservationValue function.
 * @param answer The QuestionnaireResponseItemAnswer to format.
 * @returns A human-readable string representation of the QuestionnaireResponseItemAnswer.
 */
export function formatQuestionnaireResponseItemAnswer(
  answer?: QuestionnaireResponseItemAnswer,
): string {
  if (!answer) {
    return "";
  }
  if (answer?.valueQuantity) {
    switch (answer.valueQuantity.unit) {
      case "F":
        return `${answer.valueQuantity.value} °${answer.valueQuantity.unit}`;
      case "C":
        return `${answer.valueQuantity.value} °${answer.valueQuantity.unit}`;
      default:
        return formatQuantity(answer.valueQuantity);
    }
  }
  if (answer?.valueCoding) {
    return formatCoding(answer.valueCoding);
  }
  if (typeof answer?.valueString === "string") {
    return answer.valueString;
  }
  if (answer?.valueInteger) {
    return answer.valueInteger.toString();
  }
  return "";
}

export type GroupGetterFn<FieldNameType extends string> = (
  fieldName: FieldNameType,
  _response?: QuestionnaireResponse | QuestionnaireResponseItemGroup,
) => QuestionnaireResponseItemGroup<FieldNameType> | undefined;
export type CodingGetterFn<FieldNameType extends string> = (
  fieldName: FieldNameType,
  _response?: QuestionnaireResponse | QuestionnaireResponseItemGroup,
) => Coding | undefined;
export type ValueGetterFn<FieldNameType extends string> = (
  fieldName: FieldNameType,
  _response?: QuestionnaireResponse | QuestionnaireResponseItemGroup,
) => QuestionnaireResponseItemAnswer | undefined;

export type ReverseResponseFnMappingType<FieldNameType extends string> = Record<
  string,
  ({
    origFieldName,
    getGroup,
    getCoding,
    getValue,
  }: {
    origFieldName: string;
    getGroup: GroupGetterFn<FieldNameType>;
    getCoding: CodingGetterFn<FieldNameType>;
    getValue: ValueGetterFn<FieldNameType>;
  }) => unknown
>;

export function getFormValuesFromQuestionnaireResponse<FieldNameType extends string>(
  response: QuestionnaireResponse,
  reverseResponseFnMapping: ReverseResponseFnMappingType<FieldNameType>,
) {
  if (!response.item || response.item.length === 0) return {};

  const getGroup: GroupGetterFn<FieldNameType> = (fieldName, _response = response) => {
    const responseItem = _response.item!.find((item) => item.linkId === `${fieldName}-group`);
    if (!responseItem) {
      return undefined;
    }
    return responseItem as QuestionnaireResponseItemGroup<FieldNameType>;
  };

  const getCoding: CodingGetterFn<FieldNameType> = (fieldName, _response = response) => {
    const responseItem = getGroup(fieldName, _response);
    const codingItem = responseItem?.item.find((item) => item.linkId === `${fieldName}-coding`);
    if (!codingItem) {
      console.warn("Missing coding item for field %s", fieldName);
      return undefined;
    }
    if (!codingItem.answer || codingItem.answer.length === 0) {
      console.warn("Missing answer for field %s", fieldName);
      return undefined;
    }
    if (!codingItem.answer[0].valueCoding) {
      console.warn("Missing valueCoding in index 0 of answer for field %s", fieldName);
      return undefined;
    }
    return codingItem.answer[0].valueCoding;
  };

  const getValue: ValueGetterFn<FieldNameType> = (fieldName, _response = response) => {
    const responseItem = getGroup(fieldName, _response);
    const valueItem = responseItem?.item.find((item) => item.linkId === `${fieldName}-value`);
    if (!valueItem) {
      console.warn("Missing value item for field %s", fieldName);
      return undefined;
    }
    if (!valueItem.answer || valueItem.answer.length === 0) {
      console.warn("Missing answer for field %s", fieldName);
      return undefined;
    }
    return valueItem.answer[0];
  };

  return mapValues(reverseResponseFnMapping, (reverseFn, origFieldName) =>
    reverseFn({
      origFieldName,
      getGroup,
      getCoding,
      getValue,
    }),
  );
}
